import { useMemo } from 'react';
import { Button, Dropdown, MenuProps, Row } from 'antd';
import { CheckOutlined, SortDescendingOutlined } from '@ant-design/icons';
import { Order } from '../../../store/types';
import { useSearchParams } from '../../../hooks/useSearchParams';
import { getValidSearchParams } from '../../../utils';

import styles from './index.module.scss';

export type OrderColumn =
  | 'numberOfLikes'
  | 'numberOfDislikes'
  | 'mostCommented'
  | 'createdAt';

interface QueryValues {
  orderBy: Order;
  orderByColumn: OrderColumn;
}

const createKey = ({ orderByColumn, orderBy }: QueryValues): string => `${orderByColumn}_${orderBy}`;

const DEFAULT_ORDER_BY_COLUMN: OrderColumn = 'createdAt';
const DEFAULT_ORDER_BY: Order = 'DESC';

const menuData: { label: string; orderByColumn: OrderColumn; orderBy: Order }[] = [
  { label: 'Most liked', orderByColumn: 'numberOfLikes', orderBy: 'DESC' },
  { label: 'Less liked', orderByColumn: 'numberOfLikes', orderBy: 'ASC' },
  { label: 'Most disliked', orderByColumn: 'numberOfDislikes', orderBy: 'DESC' },
  { label: 'Less disliked', orderByColumn: 'numberOfDislikes', orderBy: 'ASC' },
  { label: 'Newest', orderByColumn: 'createdAt', orderBy: 'DESC' },
  { label: 'Oldest', orderByColumn: 'createdAt', orderBy: 'ASC' },
  { label: 'Most commented', orderByColumn: 'mostCommented', orderBy: 'DESC' },
];

function SortBy() {
  const { searchParams, replaceSearchParams } = useSearchParams();
  const { orderBy, orderByColumn } = getValidSearchParams<QueryValues>(
    '*',
    searchParams,
    {
      orderByColumn: DEFAULT_ORDER_BY_COLUMN,
      orderBy: DEFAULT_ORDER_BY,
    },
  );
  const key = createKey({ orderByColumn, orderBy });

  const sortMenu: MenuProps = useMemo(
    () => ({
      style: { width: 200 },
      items: [
        ...menuData.map(({ label, orderByColumn: itemOrderByColumn, orderBy: itemOrderBy }) => {
          const currentKey = createKey({ orderByColumn: itemOrderByColumn, orderBy: itemOrderBy });

          return {
            key: currentKey,
            label: (
              <Row justify="space-between" align="middle" gutter={16} className={styles.row}>
                {label}
                {' '}
                {currentKey === key ? <CheckOutlined style={{ margin: '0 8px 0 16px' }} /> : null}
              </Row>
            ),
            onClick: () => replaceSearchParams({ orderByColumn: itemOrderByColumn, orderBy: itemOrderBy }),
          };
        }),
      ],
    }),
    [key, searchParams],
  );

  return (
    <Dropdown className={styles.order} menu={sortMenu} trigger={['click']} placement="bottomRight">
      <Button icon={<SortDescendingOutlined />}>Sort</Button>
    </Dropdown>
  );
}

export default SortBy;
